import { Injectable } from '@angular/core';
import { PhotoNotes } from 'src/providers/photo-notes/photo-notes';
import { ModalController } from '@ionic/angular';
import { CameraProvider } from 'src/shared/providers/camera/camera';
import { ConfirmPageParamsModel, ConfirmPage } from 'src/pages/confirm/confirm.component';
import { Observable, Subject } from 'rxjs';
import { PhotoNoteUrlModel } from 'src/shared/components/photo-caption/photo-caption.component';
import { PhotoNoteRoomModel } from 'src/models/to-do.model';

@Injectable({
  providedIn: 'root'
})
export class PhotoCaptionService {
  constructor(
    private camera: CameraProvider,
    private photoNotes: PhotoNotes,
    private modalCtrl: ModalController
  ) {}

  private deleteImage: Subject<void> = new Subject<void>();
  deleteImage$: Observable<void> = this.deleteImage.asObservable();

  mountPhotoUrls(collection: PhotoNoteRoomModel[], type) : PhotoNoteUrlModel[] {
    if (!collection) {
      return [];
    }
    return collection.filter(photoNote => photoNote.type === type).map(photoNote => ({
      url: photoNote.photo_url,
      id: photoNote.id,
      type: photoNote.type,
      // Address photo notes come back from the customer API with `caption`.
      photoNoteCaption: photoNote.photo_note_caption ?? photoNote.caption,
      captionChanged: false,
      deleted: false
    }));
  }

  async uploadPhotosGeneric(photoNoteUrls: PhotoNoteUrlModel[], type: string, savePhotoNoteFunction, saveCaptionFunction, roomType) {
    const localPhotoNotes = photoNoteUrls.filter((photoNote) => photoNote.id === -1);

    for (const photoNote of localPhotoNotes) {
      const photo = await this.photoNotes.uploadPictureToS3(photoNote.url, type);
      const photoUrl = roomType === 'room' ? photo.Key : photo.Location;
      const caption = photoNote.photoNoteCaption || null;
      const photoNoteUploaded = await savePhotoNoteFunction(photoUrl, caption);

      photoNote.id = photoNoteUploaded.id;
    }

    const remotePhotoNotes = photoNoteUrls.filter((photoNote) => photoNote.id > -1);

    for (const photoNote of remotePhotoNotes) {
      if (photoNote.captionChanged) {
        await saveCaptionFunction(photoNote.id, photoNote.photoNoteCaption);
      }
    }
  }

  async getImg(name: string): Promise<PhotoNoteUrlModel> {
    const photoUrl = await this.camera.getImg(true);

    return {
      url: photoUrl.dataUrl,
      type: name,
      id: -1,
      photoNoteCaption: '',
      captionChanged: false,
      deleted: false
    }
  }

  async getMultipleImages(name: string): Promise<PhotoNoteUrlModel[]> {
    const photoUrls = await this.camera.getMultipleImages(true);
    return photoUrls.map(photoUrl => ({
      url: photoUrl.dataUrl,
      type: name,
      id: -1,
      photoNoteCaption: '',
      captionChanged: false,
      deleted: false
    }));
  }

  async confirmDelete(photoId: number, photoNoteUrls: PhotoNoteUrlModel[]) {
    await Promise.all(photoNoteUrls.map(async (photoNote, _index) => {
      if (photoNote.id === photoId) {
        const params: ConfirmPageParamsModel = {
          title: 'Delete Photo?',
          body: 'Delete Photo?',
          backText: 'Go Back',
          confirmText: 'Delete',
          confirmAction: this.deletePhotoNote.bind(this, photoId)
        }
        const confirmationModal = await this.modalCtrl.create({
          component: ConfirmPage,
          componentProps: params,
          animated: false,
          cssClass: 'confirm-modal'
        });
        await confirmationModal.present();
      }
    }));
  }

  async deletePhotoNote(photoId: number) {
    try {
      await this.photoNotes.deletePhotoNotes(photoId);
      this.announceDeleteImage();
      this.modalCtrl.dismiss();
    } catch(err) {
      throw err;
    }
  }

  announceDeleteImage() {
    this.deleteImage.next();
  }
}
